import { Box } from "@mui/material";
import { ReactNode } from "react";

import { NgpVanDatabaseModeGqlType } from "~frontend/__generated__/graphql";
import { ListTagsWithIntegrationsTag } from "~frontend/graphql/tags";

export function renderIntegrations(
  tag: ListTagsWithIntegrationsTag
): ReactNode {
  const names = tag.integrationActions.flatMap(integrationAction =>
    integrationAction.ngpVanMyVotersActions
      .map(() => `${integrationAction.integration.name} > MyVoters`)
      .concat(
        integrationAction.ngpVanMyCampaignActions.map(
          () => `${integrationAction.integration.name} > MyCampaign`
        )
      )
  );

  return (
    <Box>
      {names.map((name, idx) => (
        <Box
          key={`${name}-${idx}`}
          title={name}
          sx={{
            maxWidth: "300px",
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </Box>
      ))}
    </Box>
  );
}

type NgpVanAction =
  ListTagsWithIntegrationsTag["integrationActions"][0]["ngpVanMyVotersActions"][0];

function renderSingleAction(action: NgpVanAction): ReactNode {
  let name: string = "";

  switch (action.__typename) {
    case "NgpVanTagIntegrationActionApplyActivistCode":
      if (action.activistCode) {
        name = `Activist Code: ${action.activistCode.name}`;
      } else {
        name = `Activist Code: ID ${action.activistCodeId} (not found)`;
      }
      break;

    case "NgpVanTagIntegrationActionApplySurveyResponse": {
      if (action.surveyQuestion) {
        const response = action.surveyQuestion.responses.find(
          response => response.surveyResponseId === action.surveyResponseId
        );
        if (response) {
          name = `Survey: ${action.surveyQuestion.name} - ${response.name}`;
        } else {
          name = `Survey: ${action.surveyQuestion.name} - Response ${action.surveyResponseId} (response not found)`;
        }
      } else {
        name = `Survey: Question ${action.surveyQuestionId} - Response ${action.surveyResponseId} (question not found)`;
      }
      break;
    }

    case "NgpVanTagIntegrationActionApplyResultCode": {
      if (action.resultCodeName) {
        name = `Canvass Response: ${action.resultCodeName}`;
      } else {
        name = `Canvass Response: ID ${action.resultCodeId} (not found)`;
      }
      break;
    }
  }

  return (
    <Box
      key={name}
      title={name}
      sx={{
        maxWidth: "400px",
        display: "block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {name}
    </Box>
  );
}

export function renderActions(tag: ListTagsWithIntegrationsTag): ReactNode {
  const actions: NgpVanAction[] = tag.integrationActions.flatMap(
    integrationAction =>
      integrationAction.ngpVanMyVotersActions.concat(
        integrationAction.ngpVanMyCampaignActions
      )
  );

  return <Box>{actions.map(renderSingleAction)}</Box>;
}

export function renderActionsForIntegration(
  tag: ListTagsWithIntegrationsTag,
  integrationId: string,
  databaseMode: NgpVanDatabaseModeGqlType
): ReactNode {
  let actions: NgpVanAction[] = [];

  const integrationAction = tag.integrationActions.find(
    action => action.integration.id === integrationId
  );
  if (integrationAction) {
    if (databaseMode === "MY_VOTERS") {
      actions = integrationAction.ngpVanMyVotersActions;
    } else {
      actions = integrationAction.ngpVanMyCampaignActions;
    }
  }

  return <Box>{actions.map(renderSingleAction)}</Box>;
}
